import React, { useState, useEffect } from 'react';
import Checkbox from '../ui/checkbox';
import styles from './AssignAppointmentModal.module.css';
import Button from '../ui/button';
import { getUsersActivosAndPartes } from '../../services/apiService';
import { showErrorToast } from '../../utils/notifications';

/**
 * Modal para asignar una cita a un usuario activo
 * Props:
 * - open: boolean (si el modal está visible)
 * - appointmentId: number (ID de la cita a asignar)
 * - onAssign: función para asignar la cita (recibe usuarioId)
 * - onCancel: función para cancelar la asignación
 */
export default function AssignAppointmentModal({ 
  open, 
  appointmentId,
  onAssign, 
  onCancel 
}) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUrgente, setIsUrgente] = useState(false);

  // Cargar usuarios activos con partes asignados cuando se abre el modal
  useEffect(() => {
    
    const loadUsers = async () => {
      if (open) {
        try {
          setLoading(true);
          const activeUsers = await getUsersActivosAndPartes();
          setUsers(activeUsers);
        } catch (error) {
          console.error('Error loading active users:', error);
          showErrorToast('Error al cargar los usuarios activos');
        } finally {
          setLoading(false);
        }
      }
    };

    loadUsers();
  }, [open, appointmentId]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  const handleConfirm = () => {
    if (selectedUser && onAssign) {
      onAssign(selectedUser.usuarioId, !!isUrgente);
      setSelectedUser(null);
      setIsUrgente(false);
    }
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setIsUrgente(false);
    if (onCancel) {
      onCancel();
    }
  };

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div>
              <h3 className={styles.title}>Asignar Cita</h3>
              <p className={styles.subtitle}>Selecciona un usuario para asignar la cita</p>
            </div>
          </div>
          <button className={styles.closeButton} onClick={handleCancel}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Cargando usuarios...</p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <Checkbox
                  label="Urgente"
                  checked={isUrgente}
                  onChange={(v) => setIsUrgente(typeof v === 'boolean' ? v : (v && v.target ? v.target.checked : false))}
                />
              </div>
              <div className={styles.userList}>
                {users.map((user) => (
                  <div
                    key={user.usuarioId}
                    className={`${styles.userItem} ${
                      selectedUser?.usuarioId === user.usuarioId ? styles.selected : ''
                    }`}
                    onClick={() => handleUserSelect(user)}
                  >
                    <div className={styles.userInfo}>
                      <div className={styles.userName}>
                        {user.nombre}
                      </div>
                    </div>
                    <div className={styles.rightContent}>
                      {user.partesAsignadas > 0 && (
                        <div className={styles.partesBadge}>{user.partesAsignadas} partes</div>
                      )}
                      {selectedUser?.usuarioId === user.usuarioId && (
                        <div className={styles.checkmark}>✓</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {users.length === 0 && !loading && (
                <div className={styles.emptyState}>
                  <p>No hay usuarios activos disponibles</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!selectedUser || loading}
          >
            Asignar Cita
          </Button>
        </div>
      </div>
    </div>
  );
}